import { type useUserAvailabilityModalMutation } from '#app/__generated__/relay/useUserAvailabilityModalMutation.graphql.js';
import { type useUserAvailabilityModalQuery } from '#app/__generated__/relay/useUserAvailabilityModalQuery.graphql.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { getMaxWorkPreferenceAvailabilityDate } from '#app/components/WorkAvailability/utilities.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { sessionStorage } from '#app/services/storage.js';
import { graphql, useLazyLoadQuery } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useMemo } from 'react';

type UseUserAvailabilityModalReturn = {
  /**
   * @returns response.maybeOpenUserAvailabilityModal - opens the user availability modal if the user meets the requirements
   */
  maybeOpenUserAvailabilityModal: () => void;
};

const USER_AVAILABILITY_MODAL_SEEN_SESSION_KEY =
  'CONTRA_SESSION_USER_AVAILABILITY_MODAL_SEEN_SESSION_KEY';

export const useUserAvailabilityModal = (): UseUserAvailabilityModalReturn => {
  const toast = useToast();

  const { track } = useAnalytics();

  const { visitor } = useLazyLoadQuery<useUserAvailabilityModalQuery>(
    graphql`
      query useUserAvailabilityModalQuery {
        visitor {
          userAccount {
            profile {
              hasCompletedIndyOnboarding
              workPreferences {
                isCurrentlyAvailable
                blockAvailabilityReminder
              }
            }
          }
        }
      }
    `,
    {},
  );

  const isAvailable = Boolean(
    visitor.userAccount?.profile.workPreferences?.isCurrentlyAvailable,
  );

  const { selectedUserType } = useSelectedUserType();

  const meetRequirementsToOpenModal = useMemo(() => {
    if (!visitor.userAccount?.profile || selectedUserType === 'CLIENT') {
      return false;
    }

    if (sessionStorage.getItem(USER_AVAILABILITY_MODAL_SEEN_SESSION_KEY)) {
      return false;
    }

    const { hasCompletedIndyOnboarding, workPreferences } =
      visitor.userAccount.profile;

    const workPreferencesRequirementsMet = Boolean(
      hasCompletedIndyOnboarding &&
        !workPreferences.isCurrentlyAvailable &&
        !workPreferences.blockAvailabilityReminder,
    );

    return workPreferencesRequirementsMet;
  }, [selectedUserType, visitor]);

  const [updateWorkPreferences] =
    useContraMutation<useUserAvailabilityModalMutation>(graphql`
      mutation useUserAvailabilityModalMutation(
        $input: UpdateWorkPreferencesInput!
      ) {
        updateWorkPreferences(input: $input) {
          errors {
            __typename
            message
          }
          userProfile {
            ...ProfileSidebarAvatarV2_userProfile
          }
        }
      }
    `);

  const onToggleChange = useCallback(
    (acceptingNewClients: boolean) => {
      track('switch', {
        action: 'tapped',
        switch_name: 'availability_reminder_toggle',
        switch_value: acceptingNewClients,
      });
      updateWorkPreferences({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            toast.success('Your work preferences have been updated.');
            sessionStorage.setItem(
              USER_AVAILABILITY_MODAL_SEEN_SESSION_KEY,
              'true',
            );
          }
        },
        onError: (error) => {
          toast.error('An unexpected error occurred. Please try again.');
          captureException(error);
        },
        variables: {
          input: {
            availableUntil:
              getMaxWorkPreferenceAvailabilityDate().split('T')[0],
          },
        },
      });
    },
    [toast, track, updateWorkPreferences],
  );

  const onAvailabilityRemindersTurnedOff = useCallback(() => {
    updateWorkPreferences({
      onCompleted: (errors) => {
        const { apiErrors, hasErrors, payloadErrors } = errors;
        if (hasErrors) {
          if (payloadErrors) {
            toast.error('An unexpected error occurred. Please try again.');
            captureException(payloadErrors[0]);
          } else if (apiErrors) {
            toast.error(apiErrors[0].message);
          }
        } else {
          const toastMessage = (
            <Text textStyle="subtitleRegular">
              You have successfully turned off availability reminders.
            </Text>
          );
          toast.success(toastMessage);
        }
      },
      onError: (error) => {
        toast.error('An unexpected error occurred. Please try again.');
        captureException(error);
      },
      variables: {
        input: {
          blockAvailabilityReminder: true,
        },
      },
    });
  }, [toast, updateWorkPreferences]);

  const onModalClose = useCallback(() => {
    sessionStorage.setItem(USER_AVAILABILITY_MODAL_SEEN_SESSION_KEY, 'true');
  }, []);

  const { openModal } = useModal({
    identifier: 'UserAvailabilityModal',
    modalProps: {
      isCurrentlyAvailable: isAvailable,
      onAvailabilityRemindersTurnedOff,
      onModalClose,
      onToggleChange,
    },
  });

  const maybeOpenUserAvailabilityModal = useCallback(() => {
    if (meetRequirementsToOpenModal) {
      openModal();
    }
  }, [meetRequirementsToOpenModal, openModal]);

  return { maybeOpenUserAvailabilityModal };
};
